import { useState, useRef, useLayoutEffect } from 'react';
import { TT } from '@features/ai-prompting/terminalTokens';
import { TermWindow } from './Term';
import SandboxShell from './SandboxShell';
import EvaluationTrace from './EvaluationTrace';
import ResultPanel from './ResultPanel';
import { useRunState, submitLabel } from '@features/ai-prompting/hooks/useRunState';

/**
 * Serves Build, Complete, Repair, and Transfer — all variations on "one
 * editor, submit, see result" (spec Part 4). One component, driven by
 * rep.config/rep.format, per the original v2 architecture decision; the
 * only branch is Repair's diagnosis multi-select + read-only original.
 */
const MAX_HEIGHT = 520;
const PROSE_MAX_WIDTH = 640; // ~70ch — instruction/explanatory text only, never the work surfaces

const AutoGrowTextarea = ({ value, onChange, disabled, placeholder, minHeight }) => {
  const ref = useRef(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), MAX_HEIGHT)}px`;
  }, [value, minHeight]);

  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder}
      style={{
        display: 'block', width: '100%', boxSizing: 'border-box', padding: 14,
        border: 'none', background: disabled ? TT.bgElevated : TT.bgSurface,
        color: TT.text, fontFamily: TT.fontMono, fontSize: 13.5, lineHeight: 1.6,
        resize: 'none', minHeight, maxHeight: MAX_HEIGHT, overflowY: 'auto',
      }}
    />
  );
};

/** Restores the terminal chrome (part 4 item 3) — the same dot-header
 * frame as the map page's `castle-atlas --skill-profile` panel, reused
 * verbatim via TermWindow rather than a new treatment. The amber
 * "you're revising a real attempt" cue (Part 7) moves to this outer
 * frame's border now that the textarea itself no longer has one. */
const EditorWindow = ({ repId, revising, children }) => (
  <TermWindow
    title={`castle-atlas --rep ${repId}`}
    style={revising ? { border: `1px solid ${TT.amber}`, boxShadow: `0 0 0 1px ${TT.amber}33` } : {}}
  >
    {children}
  </TermWindow>
);

/** Repair's reference copy (part 4 item 4) — same terminal chrome as the
 * editor, labelled as read-only, with a one-line caption explaining why
 * it's there. Read-only: this is what they're repairing, not a second
 * input, so editing it destroys the reference (the bug this fixes). */
const OriginalPromptBlock = ({ text }) => (
  <TermWindow title="original — read only">
    <div style={{ padding: '10px 14px 0' }}>
      <p style={{ fontFamily: TT.fontSans, fontSize: 11, color: TT.textDim, lineHeight: 1.5, margin: 0 }}>
        the prompt you are repairing. it stays here so you can compare.
      </p>
    </div>
    <div style={{
      padding: 14, color: TT.textMuted, fontFamily: TT.fontMono, fontSize: 13.5, lineHeight: 1.6,
      whiteSpace: 'pre-wrap',
    }}>
      {text}
    </div>
  </TermWindow>
);

/** Full-row click target (capped width — a handful of words shouldn't
 * stretch across the full editor column), indicator before the label like
 * a checkbox, status never color-alone (acceptance criterion 12). */
const DiagnosisRow = ({ option, selected, onToggle, disabled, postEvalState }) => {
  const glyph = postEvalState === 'correct' ? '✓'
    : postEvalState === 'missed' ? '—'
    : postEvalState === 'incorrect' ? '✗'
    : selected ? '●' : '○';
  const stateColor = postEvalState === 'correct' ? TT.green
    : postEvalState === 'missed' ? TT.amber
    : postEvalState === 'incorrect' ? '#ef4444'
    : selected ? TT.orange : TT.textDim;

  return (
    <button
      onClick={() => !disabled && onToggle(option)}
      disabled={disabled}
      className="ps-focusable"
      style={{
        display: 'flex', alignItems: 'flex-start', gap: 8,
        width: '100%', textAlign: 'left', padding: '8px 10px', marginBottom: 6,
        borderRadius: TT.radius.sm, cursor: disabled ? 'default' : 'pointer',
        background: selected ? `${TT.orange}14` : 'transparent',
        border: `1px solid ${selected ? TT.orange : TT.border}`,
        fontFamily: TT.fontSans, fontSize: 13, color: TT.text,
      }}
    >
      <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: stateColor, flexShrink: 0, width: 14, lineHeight: 1.5 }}>{glyph}</span>
      <span>{option}</span>
    </button>
  );
};

export const RepEditor = ({ rep, lessonName, principle, topBar, accent, onContinue }) => {
  const initialText = rep.config?.preloadedText ?? '';
  const [text, setText] = useState(initialText);
  const [diagnosis, setDiagnosis] = useState([]);
  const run = useRunState(rep.rep_id);
  const { phase, result, traceEvents, errorMessage, showCriteria, submit, isLocked, isFailed, isSystemError } = run;

  const diagnosisOptions = rep.config?.diagnosisOptions;
  const isRepair = Array.isArray(diagnosisOptions) && diagnosisOptions.length > 0;

  // p1.l1.r2 really does carry minWords:20 — the gate isn't broken — but
  // counting the preloaded seed the learner didn't write is misleading
  // before they've touched it. Hide the counter until the text actually
  // differs from what loaded in, then count the whole thing (that full
  // text is what's scored against the constraint either way).
  const hasEdited = text !== initialText;
  const wordCount = text.trim() ? text.trim().split(/\s+/).length : 0;
  const showCounter = rep.hasLengthConstraint && hasEdited;

  // A blank Build editor needs real room to write in; a Repair/Complete
  // editor that opens already holding a line or two of preloaded text
  // doesn't need 280px of empty box around it.
  const editorMinHeight = initialText ? 140 : 280;

  const toggleDiagnosis = (option) => {
    setDiagnosis(prev => prev.includes(option) ? prev.filter(d => d !== option) : [...prev, option]);
  };

  const submission = isRepair ? { text, diagnosis } : { text };
  const canSubmit = text.trim().length > 0 && (!isRepair || diagnosis.length > 0);
  const baseLabel = isRepair ? '$ diagnose and run' : '$ submit';
  const label = submitLabel(phase, baseLabel);

  const handleSubmitClick = () => {
    if (phase === 'pass') { onContinue?.(); return; }
    if (phase === 'fail' || phase === 'error') { submit(submission); return; }
    if (canSubmit) submit(submission);
  };

  const breakdownByOption = Object.fromEntries((result?.deterministic?.diagnosisBreakdown ?? []).map(b => [b.option, b]));
  const hasBreakdown = !!result?.deterministic?.diagnosisBreakdown;

  const revealedCriteria = showCriteria && result
    ? (result.llm?.feedback?.map(f => ({ name: f.criterion, passed: f.ok }))
      ?? result.deterministic?.checks?.map(c => ({ name: c.name, passed: c.passed }))
      ?? null)
    : null;

  // Part 4 item 5: title is now owned solely by the work-area header
  // (SandboxShell) — the rail no longer repeats it.
  const rail = {
    lessonName,
    principle,
    goal: rep.config?.goal,
    constraints: rep.config?.constraints ?? [],
    dimensions: rep.evaluatedOnDimensions ?? [],
    needsHiddenCases: rep.needsHiddenCases,
    revealedCriteria,
  };

  const hasRightContent = run.isRunning || traceEvents.length > 0 || phase === 'pass' || phase === 'fail';
  const traceAndResult = (
    <>
      <EvaluationTrace events={traceEvents} />
      {(phase === 'pass' || phase === 'fail') && <ResultPanel result={result} save={run.save} />}
    </>
  );

  return (
    <SandboxShell
      accent={accent}
      topBar={topBar}
      rail={rail}
      formatLabel={rep.format}
      repTitle={rep.title}
      // Repair always splits — the right column carries the original
      // prompt, which must stay visible from the start (that's the fix,
      // not just a layout preference: editing the only copy destroys the
      // reference). The other three formats only split once there's
      // something to show on the right.
      splitLayout={isRepair || hasRightContent}
      leftColumn={
        <div>
          <div style={{ fontSize: 12, color: TT.textDim, fontStyle: 'italic', lineHeight: 1.6, marginBottom: 4 }}>{'// instruction'}</div>
          <p style={{ fontFamily: TT.fontSans, fontSize: 13.5, color: TT.text, lineHeight: 1.6, margin: '0 0 20px', maxWidth: PROSE_MAX_WIDTH }}>
            {rep.instruction}
          </p>

          {isRepair && (
            <div style={{ marginBottom: 16, maxWidth: 480 }}>
              <div style={{ fontFamily: TT.fontMono, fontSize: 10, color: TT.textDim, textTransform: 'uppercase', marginBottom: 8 }}>
                what's wrong with it? (select all that apply)
              </div>
              {diagnosisOptions.map(option => (
                <DiagnosisRow
                  key={option}
                  option={option}
                  selected={diagnosis.includes(option)}
                  onToggle={toggleDiagnosis}
                  disabled={isLocked}
                  postEvalState={
                    !hasBreakdown ? null
                    : breakdownByOption[option]
                      ? (breakdownByOption[option].correct
                        ? (breakdownByOption[option].selected ? 'correct' : 'missed')
                        : 'incorrect')
                      : null
                  }
                />
              ))}
            </div>
          )}

          <EditorWindow repId={rep.rep_id} revising={isFailed}>
            <AutoGrowTextarea
              value={text}
              onChange={setText}
              disabled={isLocked}
              placeholder="write your prompt..."
              minHeight={editorMinHeight}
            />
          </EditorWindow>
          {showCounter && (
            <div style={{ textAlign: 'right', fontFamily: TT.fontMono, fontSize: 11, color: TT.textDim, marginTop: 4 }}>
              {wordCount} words
            </div>
          )}

          <div style={{ marginTop: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={handleSubmitClick}
              disabled={phase === 'idle' && !canSubmit}
              className="ps-focusable"
              style={{
                padding: '10px 20px', borderRadius: TT.radius.sm, fontFamily: TT.fontMono,
                fontSize: 13, fontWeight: 700, cursor: (phase === 'idle' && !canSubmit) ? 'not-allowed' : 'pointer',
                background: 'transparent',
                color: isFailed ? TT.amber : accent,
                border: `1px solid ${isFailed ? TT.amber : accent}`,
                opacity: (phase === 'idle' && !canSubmit) ? 0.4 : 1,
              }}
            >
              {label}
            </button>
            {isSystemError && (
              <span style={{ fontFamily: TT.fontMono, fontSize: 12, color: TT.cyan }}>⚠ {errorMessage}</span>
            )}
          </div>
        </div>
      }
      rightColumn={
        isRepair
          ? (
            <div>
              <OriginalPromptBlock text={initialText} />
              {hasRightContent && <div style={{ marginTop: 20 }}>{traceAndResult}</div>}
            </div>
          )
          : (hasRightContent ? traceAndResult : null)
      }
    />
  );
};

export default RepEditor;
